import { durationToMs, queryDuration } from '@bloo-team/api-client'
import type { SearchRow, SearchWindow } from '@bloo-team/api-client'

/**
 * Timeline support: turning a timeslice count result into evenly spaced
 * buckets, and the pure decisions around it (slice size, query span, which
 * grammar form the scope's engine accepted).
 */

export interface TimeBucket {
  /** Bucket start, epoch ms, local frame. */
  t: number
  count: number
}

/**
 * Slice sizes on offer, coarsest last. The pick targets ~60 buckets: fine
 * enough to see shape, coarse enough that every bucket is a clickable
 * target and the count query stays tiny.
 */
const SLICES: Array<{ label: string; ms: number }> = [
  { label: '1s', ms: 1_000 },
  { label: '5s', ms: 5_000 },
  { label: '15s', ms: 15_000 },
  { label: '1m', ms: 60_000 },
  { label: '5m', ms: 300_000 },
  { label: '15m', ms: 900_000 },
  { label: '30m', ms: 1_800_000 },
  { label: '1h', ms: 3_600_000 },
  { label: '3h', ms: 10_800_000 },
  { label: '6h', ms: 21_600_000 },
  { label: '12h', ms: 43_200_000 },
  { label: '1d', ms: 86_400_000 },
]

export function pickTimeslice(spanMs: number): { label: string; ms: number } {
  return SLICES.find((s) => spanMs / s.ms <= 90) ?? SLICES[SLICES.length - 1]!
}

/**
 * How long a window the query runs over: the duration stage when it has one,
 * else the absolute window travelling beside it. Null (no timeline) when
 * neither exists — that query's span is the whole store, and a histogram of
 * "everything" has no x-axis worth drawing.
 */
export function querySpanMs(query: string, win?: SearchWindow): number | null {
  const d = queryDuration(query)
  if (d) return durationToMs(d)
  if (win) {
    const span = new Date(win.end).getTime() - new Date(win.start).getTime()
    return Number.isFinite(span) && span > 0 ? span : null
  }
  return null
}

/**
 * A bucket timestamp as the engine might spell it: epoch ms, epoch seconds,
 * or a datetime string. The seconds range test mirrors looksLikeTimestamp's
 * reasoning — an ms value below 1e11 is before 1973 and cannot be a bucket
 * of any real query.
 */
export function toBucketMs(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value >= 1e11 && value < 1e14) return value
    if (value >= 1e8 && value < 1e11) return value * 1000
    return null
  }
  if (typeof value === 'string' && value) {
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

/**
 * Count rows -> evenly spaced buckets, empty slices filled with zero — a gap
 * in activity must render as a gap, not as adjacent bars lying about
 * continuity. Rows whose bucket time cannot be read are dropped; the fill is
 * capped so a malformed slice never allocates an unbounded array.
 */
export function buildBuckets(
  rows: SearchRow[],
  valueField: string,
  countField: string,
  sliceMs: number,
): TimeBucket[] {
  const present = new Map<number, number>()
  for (const r of rows) {
    const t = toBucketMs(r[valueField])
    const n = Number(r[countField])
    if (t === null || !Number.isFinite(n)) continue
    // Snap to the slice grid — string spellings can carry sub-slice noise.
    const snapped = Math.floor(t / sliceMs) * sliceMs
    present.set(snapped, (present.get(snapped) ?? 0) + n)
  }
  if (!present.size) return []
  const times = [...present.keys()]
  const min = Math.min(...times)
  const max = Math.max(...times)
  const span = Math.floor((max - min) / sliceMs)
  if (span > 2000) {
    // Grid fill would explode; return the present buckets, sorted, unfilled.
    return times.sort((a, b) => a - b).map((t) => ({ t, count: present.get(t)! }))
  }
  const out: TimeBucket[] = []
  for (let t = min; t <= max; t += sliceMs) out.push({ t, count: present.get(t) ?? 0 })
  return out
}

/**
 * Which column plays which role in a timeline count result — resolved by
 * NAME and value shape, never position. The select's column order is not
 * honoured by every engine (observed live: the bucket column arriving
 * second put timestamps in the split legend and emptied the chart).
 * `split` is null unless a split name is given and found.
 */
export function resolveTimelineColumns(
  fields: string[],
  rows: SearchRow[],
  /** The event-time field as this scope spells it ('CNAMTime', 'cnamtime'). */
  timeFieldSpelling: string,
  /** The field the caller asked to split by, if any. */
  splitName?: string | null,
): { count: string | null; bucket: string | null; split: string | null } {
  const count = fields.find((f) => /_col\d+$/i.test(f)) ?? null
  const rest = fields.filter((f) => f !== count)
  const named =
    rest.find((f) => f.toLowerCase() === 'timeslice') ??
    rest.find((f) => f.toLowerCase() === timeFieldSpelling.toLowerCase())
  let bucket = named ?? null
  if (!bucket) {
    const sample = rows.find((r) => rest.some((f) => r[f] != null))
    bucket = rest.find((f) => sample && toBucketMs(sample[f]) !== null) ?? rest[0] ?? null
  }
  const split = splitName
    ? (fields.find((f) => f.toLowerCase() === splitName.toLowerCase()) ??
      fields.find((f) => f !== count && f !== bucket) ??
      null)
    : null
  return { count, bucket, split }
}

export interface SplitBuckets {
  /** Top split values by total, biggest first; last may be 'Other'. */
  series: string[]
  /** Evenly spaced buckets; parts align with `series`, zero-filled. */
  data: Array<{ t: number; parts: number[]; total: number }>
}

/** How many split values get their own series before folding to Other. */
const MAX_SPLIT_SERIES = 5

/**
 * Three-column split rows -> stacked buckets. Same grid-snapping and
 * zero-filling as buildBuckets; the split dimension is capped at
 * MAX_SPLIT_SERIES + 'Other', assigned in total-desc order.
 */
export function buildSplitBuckets(
  rows: SearchRow[],
  timeField: string,
  splitField: string,
  countField: string,
  sliceMs: number,
): SplitBuckets {
  const totals = new Map<string, number>()
  const cells = new Map<number, Map<string, number>>()
  for (const r of rows) {
    const t = toBucketMs(r[timeField])
    const n = Number(r[countField])
    if (t === null || !Number.isFinite(n)) continue
    const v = String(r[splitField] ?? '—')
    const snapped = Math.floor(t / sliceMs) * sliceMs
    totals.set(v, (totals.get(v) ?? 0) + n)
    const cell = cells.get(snapped) ?? new Map<string, number>()
    cell.set(v, (cell.get(v) ?? 0) + n)
    cells.set(snapped, cell)
  }
  if (!cells.size) return { series: [], data: [] }

  const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([v]) => v)
  const named = ranked.slice(0, MAX_SPLIT_SERIES)
  const series = ranked.length > named.length ? [...named, 'Other'] : named
  const idx = new Map(named.map((v, i) => [v, i]))
  const otherIdx = series.length - 1

  const times = [...cells.keys()]
  const min = Math.min(...times)
  const max = Math.max(...times)
  const span = Math.floor((max - min) / sliceMs)
  const grid = span > 2000 ? times.sort((a, b) => a - b) : null

  const data: SplitBuckets['data'] = []
  const push = (t: number) => {
    const cell = cells.get(t)
    const parts = series.map(() => 0)
    if (cell) {
      for (const [v, n] of cell) {
        const si = idx.get(v) ?? otherIdx
        parts[si] = (parts[si] ?? 0) + n
      }
    }
    data.push({ t, parts, total: parts.reduce((s, n) => s + n, 0) })
  }
  if (grid) for (const t of grid) push(t)
  else for (let t = min; t <= max; t += sliceMs) push(t)
  return { series, data }
}

/** Epoch ms -> the `YYYY-MM-DDTHH:mm:ss` LOCAL string the invoke body wants. */
export function toLocalApiTime(ms: number): string {
  const d = new Date(ms)
  const p = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
    `T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  )
}

/* ------------------------------------------------ which grammar form worked */

/**
 * The timeslice bucket field name is unverified (see TimesliceForm in the
 * api-client) — the page probes and remembers the answer here, per scope, so
 * the probe costs one rejected invoke ONCE, not once per search. 'none'
 * records an engine that rejected both forms; the timeline then stays hidden.
 */
export type StoredTimesliceForm = 'timeslice' | 'cnamtime' | 'none'

export function timesliceFormKey(clusterId: string, scopeId: string): string {
  return `bloo.search.timeslice-form.${clusterId}.${scopeId}`
}

export function loadTimesliceForm(key: string): StoredTimesliceForm | null {
  try {
    const v = window.localStorage.getItem(key)
    if (v === 'timeslice' || v === 'cnamtime') return v
    // 'none' lives in sessionStorage only: a double rejection may be
    // query-specific (a stream without the probed time field), and burning
    // "no timeline, ever" into the scope permanently would be wrong. One
    // failed probe silences the timeline for this TAB; a new session probes
    // afresh.
    return window.sessionStorage.getItem(key) === 'none' ? 'none' : null
  } catch {
    return null
  }
}

export function saveTimesliceForm(key: string, form: StoredTimesliceForm): void {
  try {
    if (form === 'none') window.sessionStorage.setItem(key, 'none')
    else window.localStorage.setItem(key, form)
  } catch {
    // Best-effort memory; the probe simply runs again next session.
  }
}
